/**
 * HTTP API Client Adapter
 * Stubbed implementation that will call real backend endpoints
 * Currently returns errors indicating endpoints are not implemented
 */

import type { ApiClient, GetProductsParams, CreateProductInput, UpdateProductInput, GetOrdersParams, CreateOrderInput, AddCartItemInput, HomepageConfigDTO, PaginatedResponse } from '../client';
import type { Product, Order, OrderStatus, Cart, Shop, Category } from '../types';
import type { User } from '@/types';

/**
 * Get API base URL - use env var or fallback to current origin
 */
function getApiBaseUrl(): string {
  const envBase = process.env.NEXT_PUBLIC_API_BASE_URL || '';
  
  if (envBase) {
    return envBase;
  }
  
  // Client-side: use current origin (works for subdomains)
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  
  // Server-side fallback
  return '';
}

/**
 * HTTP Error class with status code
 */
export class HttpError extends Error {
  status: number;
  bodyText?: string;

  constructor(status: number, message: string, bodyText?: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

/**
 * Fetch wrapper with error handling
 * Includes credentials: 'include' to send cookies
 */
async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const baseUrl = getApiBaseUrl();
  // Ensure endpoint starts with /api (httpClient always calls with /api/...)
  const url = `${baseUrl}${endpoint}`;
  
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Required for httpOnly cookies
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      // Try to parse JSON error message if available
      let errorMessage = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.message) {
          errorMessage = errorJson.message;
        }
      } catch {
        // Not JSON, use text as-is
      }
      throw new HttpError(response.status, errorMessage || 'Request failed', errorText);
    }

    return response.json() as Promise<T>;
  } catch (error) {
    if (error instanceof HttpError) {
      throw error;
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network request failed');
  }
}

const httpClient: ApiClient = {
  auth: {
    async register(input: { email: string; password: string }): Promise<{ ok: true }> {
      return apiFetch<{ ok: true }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    async login(input: { email: string; password: string }): Promise<{ user: User }> {
      return apiFetch<{ user: User }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    async logout(): Promise<{ ok: true }> {
      return apiFetch<{ ok: true }>('/api/auth/logout', {
        method: 'POST',
      });
    },
    async me(): Promise<{ user: User }> {
      return apiFetch<{ user: User }>('/api/auth/me');
    },
    async forgotPassword(email: string): Promise<{ ok: true; token?: string }> {
      return apiFetch<{ ok: true; token?: string }>('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },
    async resetPassword(input: { token: string; newPassword: string }): Promise<{ ok: true }> {
      return apiFetch<{ ok: true }>('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    async changePassword(input: { currentPassword: string; newPassword: string }): Promise<{ ok: true }> {
      return apiFetch<{ ok: true }>('/api/auth/change-password', {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
    },
    async verifyEmail(token: string): Promise<{ ok: true }> {
      return apiFetch<{ ok: true }>('/api/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
    },
    async resendVerification(email: string): Promise<{ ok: true }> {
      return apiFetch<{ ok: true }>('/api/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },
  },
  products: {
    async getProducts(params?: GetProductsParams): Promise<PaginatedResponse<Product>> {
      const searchParams = new URLSearchParams();
      if (params?.shopId) searchParams.set('shopId', params.shopId);
      if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
      if (params?.search) searchParams.set('search', params.search);
      if (params?.limit !== undefined) searchParams.set('limit', params.limit.toString());
      if (params?.offset !== undefined) searchParams.set('offset', params.offset.toString());
      
      const query = searchParams.toString();
      const endpoint = `/api/products${query ? `?${query}` : ''}`;
      return apiFetch<PaginatedResponse<Product>>(endpoint);
    },

    async getProduct(id: string): Promise<Product | null> {
      return apiFetch<Product | null>(`/api/products/${id}`);
    },

    async createProduct(input: CreateProductInput): Promise<Product> {
      return apiFetch<Product>('/api/products', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },

    async updateProduct(id: string, patch: UpdateProductInput): Promise<Product> {
      return apiFetch<Product>(`/api/products/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },
  },
  orders: {
    async getOrders(params?: GetOrdersParams): Promise<PaginatedResponse<Order>> {
      const searchParams = new URLSearchParams();
      if (params?.userId) searchParams.set('userId', params.userId);
      if (params?.shopId) searchParams.set('shopId', params.shopId);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.limit !== undefined) searchParams.set('limit', params.limit.toString());
      if (params?.offset !== undefined) searchParams.set('offset', params.offset.toString());
      
      const query = searchParams.toString();
      const endpoint = `/api/orders${query ? `?${query}` : ''}`;
      return apiFetch<PaginatedResponse<Order>>(endpoint);
    },

    async getOrder(id: string): Promise<Order | null> {
      return apiFetch<Order | null>(`/api/orders/${id}`);
    },

    async createOrder(input: CreateOrderInput): Promise<Order> {
      return apiFetch<Order>('/api/orders', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },

    async updateOrderStatus(id: string, status: OrderStatus, byRole: 'admin' | 'seller'): Promise<Order> {
      return apiFetch<Order>(`/api/orders/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status, byRole }),
      });
    },
  },
  cart: {
    async getCart(): Promise<Cart | null> {
      return apiFetch<Cart | null>('/api/cart');
    },

    async addItem(input: AddCartItemInput): Promise<Cart> {
      return apiFetch<Cart>('/api/cart/items', {
        method: 'POST',
        body: JSON.stringify({
          productId: input.productId,
          quantity: input.quantity ?? 1,
        }),
      });
    },

    async updateItemQty(productId: string, quantity: number): Promise<Cart> {
      return apiFetch<Cart>('/api/cart/items', {
        method: 'PATCH',
        body: JSON.stringify({
          productId,
          quantity,
        }),
      });
    },

    async removeItem(productId: string): Promise<Cart> {
      return apiFetch<Cart>('/api/cart/items', {
        method: 'DELETE',
        body: JSON.stringify({
          productId,
        }),
      });
    },

    async clearCart(): Promise<void> {
      return apiFetch<void>('/api/cart', {
        method: 'DELETE',
        body: JSON.stringify({}),
      });
    },
  },
  admin: {
    async listUsers() {
      return apiFetch<{ users: Array<{ id: string; email: string; role: 'user' | 'seller' | 'admin'; createdAt?: string }> }>('/api/admin/users');
    },
    async updateUserRole(userId: string, role: 'user' | 'seller' | 'admin') {
      return apiFetch<{ user: { id: string; email: string; role: 'user' | 'seller' | 'admin'; createdAt?: string } }>(`/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      });
    },
    async getHomepageConfig() {
      return apiFetch<{ config: HomepageConfigDTO }>('/api/admin/homepage');
    },
    async updateHomepageConfig(input: HomepageConfigDTO) {
      return apiFetch<{ config: HomepageConfigDTO }>('/api/admin/homepage', {
        method: 'PUT',
        body: JSON.stringify(input),
      });
    },
    async generateDemoData(input?: { productsPerShop?: number; ordersPerShop?: number }) {
      return apiFetch<{ ok: true; created: { shops: number; products: number; orders: number } }>('/api/admin/demo/generate', {
        method: 'POST',
        body: JSON.stringify(input || {}),
      });
    },
  },
  seller: {
    async getMyShop() {
      return apiFetch<{ shop: Shop | null }>('/api/seller/shop');
    },
    async createShop(input: { name: string; contactEmail?: string; contactPhone?: string; shippingPolicy?: string; logoUrl?: string; bannerUrl?: string }) {
      return apiFetch<{ shop: Shop }>('/api/seller/shop', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    async updateMyShop(patch: Partial<{ name: string; contactEmail?: string; contactPhone?: string; shippingPolicy?: string; logoUrl?: string; bannerUrl?: string }>) {
      return apiFetch<{ shop: Shop }>('/api/seller/shop', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },
    async getOrders() {
      return apiFetch<Order[]>('/api/seller/orders');
    },
    async updateOrderStatus(orderId: string, status: OrderStatus) {
      return apiFetch<Order>(`/api/seller/orders/${orderId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
    },
  },
  wishlist: {
    async getWishlist() {
      return apiFetch<{ products: Product[] }>('/api/wishlist');
    },
    async addToWishlist(productId: string) {
      return apiFetch<{ products: Product[] }>('/api/wishlist/items', {
        method: 'POST',
        body: JSON.stringify({ productId }),
      });
    },
    async removeFromWishlist(productId: string) {
      return apiFetch<{ products: Product[] }>('/api/wishlist/items', {
        method: 'DELETE',
        body: JSON.stringify({ productId }),
      });
    },
  },
  homepage: {
    async getPublic() {
      return apiFetch<{ config: HomepageConfigDTO }>('/api/homepage');
    },
  },
  categories: {
    async getCategories(params?: { parentId?: string }) {
      const searchParams = new URLSearchParams();
      if (params?.parentId) searchParams.set('parentId', params.parentId);
      
      const query = searchParams.toString();
      const endpoint = `/api/categories${query ? `?${query}` : ''}`;
      return apiFetch<{ items: Category[] }>(endpoint);
    },
    async getCategory(id: string) {
      return apiFetch<{ item: Category }>(`/api/categories/${id}`);
    },
  },
  payments: {
    async createQPayPayment(orderId: string) {
      return apiFetch<{ paymentId: string; qrTextOrUrl: string; qrImageBase64?: string; providerInvoiceId: string }>(
        '/api/payments/qpay/create',
        {
          method: 'POST',
          body: JSON.stringify({ orderId }),
        }
      );
    },
  },
};

export default httpClient;

